// TODO: Structurally a direct port of apps/crew-merchant-ui/src/api.ts — same
// buildRequest/parseResponse/request shape and the same headers/error
// breadcrumb pattern. We swapped:
//   - `CrewMerchantApi`        → `InternalAdminApi`
//   - `crewMerchantApi`        → `internalAdminApi` (+ `internalAdminStoreApi`)
//   - URL prefix `/merchant`   → `/internal/admin`
// Migrate to a shared API-client factory once both apps consolidate.
import { CommandAdminApi, Prettify } from 'corso-types';
import { generatePath } from 'react-router-dom';
import { getAccessToken } from './accessToken';
import env from './env';
import {
  ErrorResponse,
  isApiError,
  isErrorMessage,
} from './utils/errorResponse';
import { addBreadcrumb } from './utils/observability';
import { UnsafeContextError } from './utils/unsafeContextError';

type PathParams<Path extends CommandAdminApi.ApiPath> = Parameters<
  typeof generatePath<Path>
>[1];

type ApiRequest = {
  [P in CommandAdminApi.ApiPath]: {
    [M in CommandAdminApi.MethodOfPath<P>]: Prettify<
      {
        path: P;
        method: M;
      } & (PathParams<P> extends undefined
        ? {
            params?: never;
          }
        : { params: PathParams<P> }) &
        (CommandAdminApi.RequestBody<P, M> extends never
          ? {
              body?: never;
            }
          : {
              body: CommandAdminApi.RequestBody<P, M>;
            }) &
        (CommandAdminApi.RequestQuery<P, M> extends never
          ? {
              query?: never;
            }
          : {
              query: CommandAdminApi.RequestQuery<P, M>;
            })
    >;
  }[CommandAdminApi.MethodOfPath<P>];
}[CommandAdminApi.ApiPath];

async function parseResponse<Data>(response: Response) {
  const clone = response.clone();
  const body: unknown = await clone.json();
  if (response.ok) return body as Data;

  if (isApiError(body))
    throw new ErrorResponse(body.clientMessage, response, body);
  if (isErrorMessage(body))
    throw new ErrorResponse(body.message, response, body);

  throw new ErrorResponse(
    'Something went wrong, please try again later.',
    response,
    body,
  );
}

async function buildRequest<
  Path extends CommandAdminApi.ApiPath,
  Method extends CommandAdminApi.MethodOfPath<Path>,
>(
  {
    path,
    method,
    params,
    body,
    query,
  }: Extract<ApiRequest, { path: Path; method: Method }>,
  fetchInit?: Omit<RequestInit, 'method' | 'body'>,
) {
  const { headers: initHeaders, ...init } = fetchInit ?? {};

  const accessToken = await getAccessToken();

  // TODO: handle default values here
  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
  };

  const encodedParams = params
    ? (Object.fromEntries(
        Object.entries<
          NonNullable<PathParams<Path>>[keyof NonNullable<PathParams<Path>>]
        >(params).map(([key, value]) => [
          key,
          value ? encodeURIComponent(value) : null,
        ]),
      ) as PathParams<Path>)
    : undefined;

  // LOCAL: merchant-ui prefixes `/merchant`; internal admin lives under `/internal/admin`.
  const url = new URL(
    `${env.VITE_BASE_API_URL}/internal/admin/command${generatePath(path, encodedParams)}`,
  );

  if (query) {
    Object.entries<unknown>(query).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (
        typeof value !== 'string' &&
        typeof value !== 'number' &&
        typeof value !== 'boolean'
      ) {
        return;
      }
      url.searchParams.append(key, `${value}`);
    });
  }

  const jsonBody = body ? JSON.stringify(body) : undefined;
  const headers = new Headers(defaultHeaders);
  new Headers(initHeaders).forEach((value, key) => headers.set(key, value));

  const response = await fetch(url.toString(), {
    method,
    body: jsonBody,
    headers,
    ...init,
  }).catch((error: unknown) => {
    const errorContext = {
      method,
      url: url.toString(),
      body: jsonBody,
      // keep the bearer token out of Sentry
      headers: Object.fromEntries(
        [...headers].filter(([key]) => key.toLowerCase() !== 'authorization'),
      ),
      ...init,
    };

    addBreadcrumb({
      category: 'fetch',
      message: 'API Request Failed to Fetch',
      level: 'warning',
      data: errorContext,
    });

    if (error instanceof Error) {
      throw new UnsafeContextError('API Request Failed to Fetch', {
        cause: { error, errorContext },
      });
    }

    throw new UnsafeContextError('API Request Failed to Fetch with Non-Error', {
      cause: { error, errorContext },
    });
  });

  return response;
}

async function request<
  Path extends CommandAdminApi.ApiPath,
  Method extends CommandAdminApi.MethodOfPath<Path>,
>(
  apiRequest: Extract<ApiRequest, { path: Path; method: Method }>,
  fetchInit?: Omit<RequestInit, 'method' | 'body'>,
) {
  const response = await buildRequest(apiRequest, fetchInit);
  return parseResponse<CommandAdminApi.ResponseBody<Path, Method>>(response);
}

const api = {
  login: {
    withAuth0: () =>
      request({
        path: '/login',
        method: 'post',
        params: {},
      }),
  },
  agency: {
    list: () =>
      request({
        path: '/agency',
        method: 'get',
        params: {},
      }),
  },
  frontTags: {
    list: () =>
      request({
        path: '/front-tag',
        method: 'get',
        params: {},
      }),
  },
  organizations: {
    list: () =>
      request({
        path: '/organization',
        method: 'get',
        params: {}, // TODO match merchant-ui — `PathParams<P>` resolves to `{} | undefined` for path-less endpoints, so the empty object is required.
      }),
    create: (body: CommandAdminApi.RequestBody<'/organization', 'post'>) =>
      request({
        path: '/organization',
        method: 'post',
        params: {},
        body,
      }),
  },

  shipProtectSets: {
    create: (body: CommandAdminApi.RequestBody<'/ship-protect-set', 'post'>) =>
      request({
        path: '/ship-protect-set',
        method: 'post',
        params: {},
        body,
      }),
    list: () =>
      request({
        path: '/ship-protect-set',
        method: 'get',
        params: {},
      }),
    getDetailById: (setId?: number) =>
      request({
        path: '/ship-protect-set/:setId',
        method: 'get',
        params: { setId: `${setId}` },
      }),
    listStores: (setId?: number) =>
      request({
        path: '/ship-protect-set/:setId/store',
        method: 'get',
        params: { setId: `${setId}` },
      }),
    createUnit: (
      setId: number,
      body: CommandAdminApi.RequestBody<
        '/ship-protect-set/:setId/unit',
        'post'
      >,
    ) =>
      request({
        path: '/ship-protect-set/:setId/unit',
        method: 'post',
        params: { setId: `${setId}` },
        body,
      }),
    updateUnit: (
      setId: number,
      unitId: number,
      body: CommandAdminApi.RequestBody<
        '/ship-protect-set/:setId/unit/:unitId',
        'put'
      >,
    ) =>
      request({
        path: '/ship-protect-set/:setId/unit/:unitId',
        method: 'put',
        params: { setId: `${setId}`, unitId: `${unitId}` },
        body,
      }),
    deleteUnit: (setId: number, unitId: number) =>
      request({
        path: '/ship-protect-set/:setId/unit/:unitId',
        method: 'delete',
        params: { setId: `${setId}`, unitId: `${unitId}` },
      }),
  },

  users: {
    search: (searchTerm?: string) =>
      request({
        path: '/user',
        method: 'get',
        params: {},
        query: { searchTerm },
      }),
    getById: (userId?: number) =>
      request({
        path: '/user/:userId',
        method: 'get',
        params: { userId: `${userId}` },
      }),
  },

  stores: {
    list: (query: CommandAdminApi.RequestQuery<'/store', 'get'>) =>
      request({
        path: '/store',
        method: 'get',
        params: {}, // TODO match merchant-ui — `PathParams<P>` resolves to `{} | undefined` for path-less endpoints, so the empty object is required.
        query,
      }),
  },
  analytics: {
    query: (body: CommandAdminApi.RequestBody<'/analytics/query', 'post'>) =>
      request({
        path: '/analytics/query',
        method: 'post',
        params: {},
        body,
      }),
    lists: {
      query: (
        body: CommandAdminApi.RequestBody<'/analytics/lists/query', 'post'>,
      ) =>
        request({
          path: '/analytics/lists/query',
          method: 'post',
          params: {},
          body,
        }),
    },
  },
  shippingClaims: {
    list: (body: CommandAdminApi.RequestBody<'/shipping-claim', 'post'>) =>
      request({
        path: '/shipping-claim',
        method: 'post',
        params: {},
        body,
      }),
  },
  storeAlerts: {
    publish: (body: CommandAdminApi.RequestBody<'/store-alert', 'post'>) =>
      request({
        path: '/store-alert',
        method: 'post',
        params: {},
        body,
      }),
  },
  sidekick: {
    searchConversations: (
      body: CommandAdminApi.RequestBody<
        '/sidekick/conversation/search',
        'post'
      >,
    ) =>
      request({
        path: '/sidekick/conversation/search',
        method: 'post',
        params: {},
        body,
      }),
    getConversation: (conversationId: number) =>
      request({
        path: '/sidekick/conversation/:conversationId',
        method: 'get',
        params: { conversationId: `${conversationId}` },
      }),
  },
  store: (storeId?: number) => ({
    get: () =>
      request({
        path: '/store/:storeId',
        method: 'get',
        params: { storeId: `${storeId}` },
      }),
    listInvoices: () =>
      request({
        path: '/store/:storeId/invoice',
        method: 'get',
        params: { storeId: `${storeId}` },
      }),
    listUsers: () =>
      request({
        path: '/store/:storeId/user',
        method: 'get',
        params: { storeId: `${storeId}` },
      }),
    listExtensions: () =>
      request({
        path: '/store/:storeId/extension',
        method: 'get',
        params: { storeId: `${storeId}` },
      }),
    updateAgency: (
      body: CommandAdminApi.RequestBody<'/store/:storeId/agency', 'put'>,
    ) =>
      request({
        path: '/store/:storeId/agency',
        method: 'put',
        params: { storeId: `${storeId}` },
        body,
      }),
    updateShipProtectSettings: (
      body: CommandAdminApi.RequestBody<
        '/store/:storeId/ship-protect-settings',
        'put'
      >,
    ) =>
      request({
        path: '/store/:storeId/ship-protect-settings',
        method: 'put',
        params: { storeId: `${storeId}` },
        body,
      }),
    updateFeatureFlags: (
      body: CommandAdminApi.RequestBody<'/store/:storeId/feature-flags', 'put'>,
    ) =>
      request({
        path: '/store/:storeId/feature-flags',
        method: 'put',
        params: { storeId: `${storeId}` },
        body,
      }),
    updateEmailSettings: (
      body: CommandAdminApi.RequestBody<
        '/store/:storeId/email-settings',
        'put'
      >,
    ) =>
      request({
        path: '/store/:storeId/email-settings',
        method: 'put',
        params: { storeId: `${storeId}` },
        body,
      }),
    updatePricingSet: (
      body: CommandAdminApi.RequestBody<'/store/:storeId/pricing-set', 'put'>,
    ) =>
      request({
        path: '/store/:storeId/pricing-set',
        method: 'put',
        params: { storeId: `${storeId}` },
        body,
      }),
    updateFrontSettings: (
      body: CommandAdminApi.RequestBody<
        '/store/:storeId/front-settings',
        'put'
      >,
    ) =>
      request({
        path: '/store/:storeId/front-settings',
        method: 'put',
        params: { storeId: `${storeId}` },
        body,
      }),
    updateBillingProvider: (
      body: CommandAdminApi.RequestBody<
        '/store/:storeId/billing-provider',
        'put'
      >,
    ) =>
      request({
        path: '/store/:storeId/billing-provider',
        method: 'put',
        params: { storeId: `${storeId}` },
        body,
      }),
    updateLocation: (
      locationId: number,
      body: CommandAdminApi.RequestBody<
        '/store/:storeId/location/:locationId',
        'put'
      >,
    ) =>
      request({
        path: '/store/:storeId/location/:locationId',
        method: 'put',
        params: { storeId: `${storeId}`, locationId: `${locationId}` },
        body,
      }),
    updateShippingLine: (
      shippingLineId: number,
      body: CommandAdminApi.RequestBody<
        '/store/:storeId/shipping-line/:shippingLineId',
        'put'
      >,
    ) =>
      request({
        path: '/store/:storeId/shipping-line/:shippingLineId',
        method: 'put',
        params: {
          storeId: `${storeId}`,
          shippingLineId: `${shippingLineId}`,
        },
        body,
      }),
    createContact: (
      body: CommandAdminApi.RequestBody<'/store/:storeId/contact', 'post'>,
    ) =>
      request({
        path: '/store/:storeId/contact',
        method: 'post',
        params: { storeId: `${storeId}` },
        body,
      }),
    updateContact: (
      contactId: number,
      body: CommandAdminApi.RequestBody<
        '/store/:storeId/contact/:contactId',
        'put'
      >,
    ) =>
      request({
        path: '/store/:storeId/contact/:contactId',
        method: 'put',
        params: { storeId: `${storeId}`, contactId: `${contactId}` },
        body,
      }),
    deleteContact: (contactId: number) =>
      request({
        path: '/store/:storeId/contact/:contactId',
        method: 'delete',
        params: { storeId: `${storeId}`, contactId: `${contactId}` },
      }),
    createInspectionGrade: (
      body: CommandAdminApi.RequestBody<
        '/store/:storeId/inspection-grade',
        'post'
      >,
    ) =>
      request({
        path: '/store/:storeId/inspection-grade',
        method: 'post',
        params: { storeId: `${storeId}` },
        body,
      }),
    updateInspectionGrade: (
      inspectionGradeId: number,
      body: CommandAdminApi.RequestBody<
        '/store/:storeId/inspection-grade/:inspectionGradeId',
        'put'
      >,
    ) =>
      request({
        path: '/store/:storeId/inspection-grade/:inspectionGradeId',
        method: 'put',
        params: {
          storeId: `${storeId}`,
          inspectionGradeId: `${inspectionGradeId}`,
        },
        body,
      }),
    createInvoiceDefinition: (
      body: CommandAdminApi.RequestBody<
        '/store/:storeId/invoice-definition',
        'post'
      >,
    ) =>
      request({
        path: '/store/:storeId/invoice-definition',
        method: 'post',
        params: { storeId: `${storeId}` },
        body,
      }),
    updateInvoiceDefinition: (
      invoiceDefinitionId: number,
      body: CommandAdminApi.RequestBody<
        '/store/:storeId/invoice-definition/:invoiceDefinitionId',
        'put'
      >,
    ) =>
      request({
        path: '/store/:storeId/invoice-definition/:invoiceDefinitionId',
        method: 'put',
        params: {
          storeId: `${storeId}`,
          invoiceDefinitionId: `${invoiceDefinitionId}`,
        },
        body,
      }),
    createCommissionArrangement: (
      body: CommandAdminApi.RequestBody<
        '/store/:storeId/commission-arrangement',
        'post'
      >,
    ) =>
      request({
        path: '/store/:storeId/commission-arrangement',
        method: 'post',
        params: { storeId: `${storeId}` },
        body,
      }),
    updateCommissionArrangement: (
      commissionArrangementId: number,
      body: CommandAdminApi.RequestBody<
        '/store/:storeId/commission-arrangement/:commissionArrangementId',
        'put'
      >,
    ) =>
      request({
        path: '/store/:storeId/commission-arrangement/:commissionArrangementId',
        method: 'put',
        params: {
          storeId: `${storeId}`,
          commissionArrangementId: `${commissionArrangementId}`,
        },
        body,
      }),
  }),
  organization: (organizationId?: number) => ({
    get: () =>
      request({
        path: '/organization/:organizationId',
        method: 'get',
        params: { organizationId: `${organizationId}` },
      }),
    listStores: () =>
      request({
        path: '/organization/:organizationId/store',
        method: 'get',
        params: { organizationId: `${organizationId}` },
      }),
    addStore: (
      body: CommandAdminApi.RequestBody<
        '/organization/:organizationId/store',
        'post'
      >,
    ) =>
      request({
        path: '/organization/:organizationId/store',
        method: 'post',
        params: { organizationId: `${organizationId}` },
        body,
      }),
    removeStore: (
      body: CommandAdminApi.RequestBody<
        '/organization/:organizationId/store',
        'delete'
      >,
    ) =>
      request({
        path: '/organization/:organizationId/store',
        method: 'delete',
        params: { organizationId: `${organizationId}` },
        body,
      }),
    addUser: (
      body: CommandAdminApi.RequestBody<
        '/organization/:organizationId/user',
        'post'
      >,
    ) =>
      request({
        path: '/organization/:organizationId/user',
        method: 'post',
        params: { organizationId: `${organizationId}` },
        body,
      }),
    removeUser: (
      body: CommandAdminApi.RequestBody<
        '/organization/:organizationId/user',
        'delete'
      >,
    ) =>
      request({
        path: '/organization/:organizationId/user',
        method: 'delete',
        params: { organizationId: `${organizationId}` },
        body,
      }),
  }),
};

export default api;
